// Kebab.jsx
// Client: vmfive
// Version: 1.1.0
// 32×32 overflow menu (more-horizontal trigger) that opens a popover.
// Supports regular items and danger items (red text, red-tint hover).
//
// B-1 fix: popover is rendered via ReactDOM.createPortal into document.body
// with position:fixed + JS-computed coordinates — safe inside overflow:hidden
// and CSS-transform containers.  Scroll/resize events close the popover.

import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Icon from './Icon';
import useDismiss from './useDismiss';
import './components-company-application.css';

/**
 * Kebab — props:
 *   items     {Array}   Menu items. Each item is one of:
 *                         { label, icon?, onClick, danger? } — regular item
 *                         { sep: true }                      — divider line
 *   align     {string=} 'right' (default) or 'left' — popover alignment
 *   className {string=} Extra CSS class on the wrapper span
 */
export default function Kebab({ items = [], align = 'right', className = '' }) {
  const [open, setOpen] = useState(false);
  const [popPos, setPopPos] = useState({ top: 0, left: undefined, right: undefined });

  const close = useCallback(() => setOpen(false), []);
  const [triggerRef, popRef] = useDismiss(open, close);

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopPos({
      top: rect.bottom + 4,
      left: align === 'left' ? rect.left : undefined,
      right: align !== 'left' ? window.innerWidth - rect.right : undefined,
    });
  }, [triggerRef, align]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open) {
      computePosition();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  // Close on scroll or resize
  useEffect(() => {
    if (!open) return;
    const handleClose = () => setOpen(false);
    window.addEventListener('scroll', handleClose, { capture: true, passive: true });
    window.addEventListener('resize', handleClose, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleClose, { capture: true });
      window.removeEventListener('resize', handleClose);
    };
  }, [open]);

  const popStyle = {
    top: popPos.top,
    ...(popPos.left !== undefined ? { left: popPos.left } : {}),
    ...(popPos.right !== undefined ? { right: popPos.right } : {}),
  };

  const popover = open
    ? ReactDOM.createPortal(
        <div
          ref={popRef}
          className="pop pop--fixed"
          role="menu"
          style={popStyle}
        >
          {items.map((item, i) => {
            if (item.sep) {
              return <div key={i} className="pop__sep" />;
            }
            return (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={`pop__item${item.danger ? ' danger' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  if (item.onClick) item.onClick();
                }}
              >
                {item.icon && (
                  <Icon name={item.icon} size={15} strokeWidth={1.8} />
                )}
                {item.label}
              </button>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <span
        ref={triggerRef}
        className={className}
        style={{ position: 'relative', display: 'inline-block' }}
      >
        <button
          type="button"
          className="kebab"
          aria-label="More options"
          aria-expanded={open ? 'true' : 'false'}
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <Icon name="more-horizontal" size={18} strokeWidth={1.8} />
        </button>
      </span>
      {popover}
    </>
  );
}
